import React, {useCallback, useEffect, useRef, useState} from "react"
import {Navigate, useNavigate, useParams} from "react-router-dom"
import {useConfig} from "../../hooks/useConfig"
import {useBoothSession} from "../../hooks/useBoothSession"
import {compose} from "../../utils/ImageUtil"
import {processAiEffect} from "../../library/requests/AiRequests"
import {Alert} from "react-bootstrap"
import EditImagePreview from "../edit-tools/EditImagePreview"
import EditToolbars from "../edit-tools/EditToolbars"
import RetakeButton from "../buttons/RetakeButton"
import PostButton from "../buttons/PostButton"
import DoneButton from "../buttons/DoneButton"
import "../../css/Editing.css"
import * as StickerUtil from "../../utils/StickerUtil"

function EditPage() {
  const params = useParams<"code">()
  const code = params.code!
  const navigate = useNavigate()

  const {config, loading} = useConfig(code)
  const {rawImage, aiProcessedImage, setCompositeImage, clearImages, aiImageHistory, selectedAiImageIndex, addAiImage, selectAiImage, guestInputValues} = useBoothSession(config?.presetCode ?? code)

  const sharedCanvas = useRef<HTMLCanvasElement>(null)

  const [selectedBackground, setSelectedBackground] = useState<string | null>(null)
  const [selectedOverlay, setSelectedOverlay] = useState<string | null>(null)

  // Check if background removal was expected but failed
  const backgroundRemovalFailed = config?.backgroundReplacementOn && !aiProcessedImage
  // Check if AI effect was expected but failed
  const aiEffectFailed = config?.aiEffectOn && !aiProcessedImage
  const [imageError, setImageError] = useState<string | null>(() => {
    if (aiEffectFailed) return "The AI effect could not be applied. You can regenerate or continue with the original photo."
    if (backgroundRemovalFailed) return "We couldn't remove the background. You can continue or retry by pressing Retake."
    return null
  })

  const [aiEffectRegenerationsRemaining, setAiEffectRegenerationsRemaining] = useState(0)
  const [aiEffectProcessing, setAiEffectProcessing] = useState(false)

  useEffect(() => {
    if (config?.aiEffectSequence) {
      setAiEffectRegenerationsRemaining(config.aiEffectSequence.maxNumberOfRegenerations)
    }
  }, [config?.aiEffectSequence])

  const handleUpload = useCallback(() => {
    if (!config) return

    const userImg = document.querySelector("#userImage") as HTMLImageElement
    const overlayImg = document.querySelector("#overlayImage") as HTMLImageElement
    const stickers = Array.from(
      document.querySelectorAll("#stickersDropzone > .sticker"),
    ) as HTMLImageElement[]
    const backgroundImg = document.querySelector("#selectedBackground") as HTMLImageElement

    const canvas = sharedCanvas.current
    if (!canvas) {
      throw new Error("Canvas missing")
    }

    const composedImage = compose(userImg, overlayImg, backgroundImg, stickers, canvas)
    setCompositeImage(composedImage)
    navigate(`/${config.presetCode}/wrap-up`)
  }, [config, navigate, setCompositeImage])

  const resetBooth = useCallback(() => {
    if (!config) return
    clearImages()
    navigate(`/${config.presetCode}/camera`)
  }, [config, navigate, clearImages])

  const handleAiEffectRegenerate = useCallback(async () => {
    if (!config || !rawImage) return
    setAiEffectProcessing(true)
    try {
      const imageUrl = await processAiEffect(config.presetCode, rawImage, guestInputValues)
      addAiImage(imageUrl)
      setAiEffectRegenerationsRemaining(prev => prev - 1)
    } catch {
      // Don't decrement on failure
    }
    setAiEffectProcessing(false)
  }, [config, rawImage, addAiImage, guestInputValues])

  if (loading || !config) {
    return null
  }

  // Redirect to camera if no image
  if (!rawImage) {
    return <Navigate to={`/${config.presetCode}/camera`} replace />
  }

  const theme = config.theme
  const userImage = aiImageHistory[selectedAiImageIndex] ?? aiProcessedImage ?? rawImage

  // Filter out backgrounds tool if background removal failed
  const tools = backgroundRemovalFailed
    ? config.editTools.filter(tool => tool !== "backgrounds")
    : config.editTools

  return (
    <>
      {imageError &&
        <div className="boothContainer">
          <Alert dismissible variant="secondary" onClose={() => setImageError(null)}>{imageError}</Alert>
        </div>
      }
      <canvas className="hiddenCanvas" ref={sharedCanvas}></canvas>
      <section onMouseDown={StickerUtil.handleOutsideClick}>
        <EditImagePreview
          userImage={userImage}
          currentOverlayUrl={selectedOverlay}
          currentBackgroundUrl={selectedBackground}
        />

        <EditToolbars
          config={config}
          tools={tools}
          rawImage={rawImage}
          userImage={userImage}
          onChangeBackground={setSelectedBackground}
          onChangeOverlay={setSelectedOverlay}
          aiEffectRemainingRegenerations={aiEffectRegenerationsRemaining}
          aiEffectIsProcessing={aiEffectProcessing}
          onAiEffectRegenerate={handleAiEffectRegenerate}
          aiImageHistory={aiImageHistory}
          selectedAiImageIndex={selectedAiImageIndex}
          onAiImageSelect={selectAiImage}
        />

        <div className="buttonRow">
          <RetakeButton theme={theme} onPress={resetBooth}/>
          {tools.length > 0 && config.showGalleryOptIn ?
            <DoneButton theme={theme} onPress={handleUpload}/> :
            <PostButton theme={theme} onPress={handleUpload}/>
          }
        </div>
      </section>
    </>
  )
}

export default EditPage
